import { 
  Typography, 
  Box, 
  Chip, 
  CircularProgress, 
  Alert, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  RefreshIcon
} from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import { authenticateWithPKCE } from '../services/authService'
import { useEmailList, useRefreshEmailList } from '../hooks/useEmailList'
import { useState, useEffect } from 'react'

export interface FavioWidgetProps {
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error'
  size?: 'small' | 'medium' | 'large'
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  embedId?: string
  showAuthStatus?: boolean
  showEmailList?: boolean // Mostrar lista de emails
  showDataTable?: boolean // Mostrar tabla de datos
}

const FavioWidget = ({
  color = 'primary',
  size = 'medium',
  variant = 'h3',
  embedId,
  showAuthStatus = false,
  showEmailList = false,
  showDataTable = false
}: FavioWidgetProps) => {
  const { token, setToken, isAuthenticated, setEmbedId } = useAuth()
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [hasTriedAuth, setHasTriedAuth] = useState(false)

  // Hook para obtener datos de EmailList
  const { 
    data: emailListData, 
    isLoading: isLoadingEmails, 
    error: emailListError,
    refetch: refetchEmails 
  } = useEmailList()

  const refreshEmailList = useRefreshEmailList()

  // Establecer embedId en el contexto si se proporciona
  useEffect(() => {
    if (embedId) {
      setEmbedId(embedId)
    }
  }, [embedId, setEmbedId])

  // Auto-autenticación automática al crear el widget (solo una vez)
  useEffect(() => {
    if (embedId && !isAuthenticated && !isAuthenticating && !hasTriedAuth) {
      handleAuthenticate()
    }
  }, [embedId, isAuthenticated, isAuthenticating, hasTriedAuth])

  const handleAuthenticate = async () => {
    if (!embedId) {
      setAuthError('EmbedId es requerido para autenticación')
      setHasTriedAuth(true)
      return
    }

    try {
      setIsAuthenticating(true)
      setAuthError(null)
      setHasTriedAuth(true)
      
      const newToken = await authenticateWithPKCE(embedId)
      setToken(newToken)
    } catch (error: any) {
      setAuthError(error.message || 'Error en autenticación')
    } finally {
      setIsAuthenticating(false)
    }
  }
  const getSizeProps = (size: string) => {
    switch (size) {
      case 'small':
        return { fontSize: '1.5rem', padding: 1 }
      case 'large':
        return { fontSize: '3rem', padding: 3 }
      default:
        return { fontSize: '2rem', padding: 2 }
    }
  }

  return (
    <Box
      sx={{
        textAlign: 'center',
        ...getSizeProps(size),
        '&:hover': {
          transform: 'scale(1.05)',
          transition: 'transform 0.2s ease-in-out'
        }
      }}
    >
      <Typography
        variant={variant}
        color={`${color}.main`}
        sx={{
          fontWeight: 'bold',
          textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        Favio
      </Typography>

      {/* Estado de autenticación */}
      {showAuthStatus && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          {isAuthenticating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Autenticando...
              </Typography>
            </Box>
          )}

          {isAuthenticated && !isAuthenticating && (
            <Chip
              label="🔐 Autenticado"
              color="success"
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />
          )}

          {!isAuthenticated && !isAuthenticating && !authError && embedId && (
            <Chip
              label="🔓 Autenticando..."
              color="info"
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>
      )}

      {/* Error de autenticación */}
      {authError && (
        <Alert severity="error" sx={{ mt: 1, fontSize: '0.7rem' }}>
          <strong>Error de autenticación:</strong> {authError}
        </Alert>
      )}

      {/* Lista de Emails */}
      {showEmailList && isAuthenticated && (
        <Box sx={{ mt: 2, textAlign: 'left' }}>
          <Card sx={{ maxWidth: 400, mx: 'auto' }}>
            <CardHeader
              title="📧 Email Lists"
              action={
                <Button
                  size="small"
                  onClick={() => refreshEmailList()}
                  disabled={isLoadingEmails}
                >
                  {isLoadingEmails ? <CircularProgress size={16} /> : '🔄'}
                </Button>
              }
            />
            <CardContent>
              {isLoadingEmails && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Cargando emails...
                  </Typography>
                </Box>
              )}

              {emailListError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error: {emailListError.message}
                </Alert>
              )}

              {emailListData && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Total: {emailListData.total} emails
                  </Typography>
                  {emailListData.data.slice(0, 3).map((item) => (
                    <Box key={item.emailListId} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {item.emailListName}
                      </Typography>
                      {item.emailListDescription && (
                        <Typography variant="caption" color="text.secondary">
                          {item.emailListDescription}
                        </Typography>
                      )}
                    </Box>
                  ))}
                  {emailListData.data.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      ... y {emailListData.data.length - 3} más
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tabla de Datos */}
      {showDataTable && isAuthenticated && (
        <Box sx={{ mt: 2, textAlign: 'left' }}>
          <Card sx={{ maxWidth: 600, mx: 'auto' }}>
            <CardHeader
              title="📊 Datos de EmailList"
              action={
                <Button
                  size="small"
                  onClick={() => refreshEmailList()}
                  disabled={isLoadingEmails}
                >
                  {isLoadingEmails ? <CircularProgress size={16} /> : '🔄'}
                </Button>
              }
            />
            <CardContent>
              {isLoadingEmails && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Cargando datos...
                  </Typography>
                </Box>
              )}

              {emailListError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error: {emailListError.message}
                </Alert>
              )}

              {emailListData && (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Vendor ID</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailListData.data.map((item) => (
                        <TableRow key={item.emailListId}>
                          <TableCell>{item.emailListId}</TableCell>
                          <TableCell>{item.emailListName}</TableCell>
                          <TableCell>{item.vendorId}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.isDelete === 0 ? 'Activo' : 'Eliminado'}
                              color={item.isDelete === 0 ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  )
}

export default FavioWidget
